import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, LayoutDashboard, ShoppingCart, ShoppingBag, Package, Warehouse, Wallet, BookOpen, BarChart3, ShieldAlert, Plus, Globe, ArrowRight, Sparkles, } from 'lucide-react';
import { toggleLanguage } from '../../lib/i18n';
export function CommandPalette({ isOpen, onClose }) {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);
    useEffect(() => {
        if (isOpen) {
            setSearch('');
            setSelectedIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);
    const items = [
        // Pages
        {
            id: 'page-pos',
            category: 'pages',
            title: isRtl ? 'نقطة البيع السريعة (POS)' : 'Point of Sale (POS)',
            subtitle: isRtl ? 'واجهة الكاشير الفورية وقارئ الباركود' : 'High-speed Cashier & Barcode Terminal',
            icon: _jsx(ShoppingCart, { className: "w-4 h-4 text-emerald-600" }),
            shortcut: 'POS',
            onSelect: () => {
                navigate('/pos');
                onClose();
            },
        },
        {
            id: 'page-dashboard',
            category: 'pages',
            title: t('nav.dashboard'),
            subtitle: isRtl ? 'المؤشرات والرسوم البيانية العامة' : 'Overview KPIs & Analytics',
            icon: _jsx(LayoutDashboard, { className: "w-4 h-4 text-blue-600" }),
            onSelect: () => {
                navigate('/dashboard');
                onClose();
            },
        },
        {
            id: 'page-sales',
            category: 'pages',
            title: t('nav.sales'),
            subtitle: isRtl ? 'فواتير المبيعات وعروض الأسعار' : 'Sales Invoices & Quotations',
            icon: _jsx(ShoppingCart, { className: "w-4 h-4 text-blue-600" }),
            onSelect: () => {
                navigate('/sales');
                onClose();
            },
        },
        {
            id: 'page-purchases',
            category: 'pages',
            title: t('nav.purchases'),
            subtitle: isRtl ? 'فواتير الشراء وفواتير الموردين' : 'Vendor Bills & Purchase Orders',
            icon: _jsx(ShoppingBag, { className: "w-4 h-4 text-indigo-600" }),
            onSelect: () => {
                navigate('/purchases');
                onClose();
            },
        },
        {
            id: 'page-products',
            category: 'pages',
            title: t('nav.products'),
            subtitle: isRtl ? 'دليل المنتجات، الأسعار والباركود' : 'Catalog, Barcodes & Pricing',
            icon: _jsx(Package, { className: "w-4 h-4 text-purple-600" }),
            onSelect: () => {
                navigate('/products');
                onClose();
            },
        },
        {
            id: 'page-inventory',
            category: 'pages',
            title: t('nav.inventory'),
            subtitle: isRtl ? 'أرصدة المستودعات والتحويلات' : 'Stock Levels & Warehouse Transfers',
            icon: _jsx(Warehouse, { className: "w-4 h-4 text-amber-600" }),
            onSelect: () => {
                navigate('/inventory');
                onClose();
            },
        },
        {
            id: 'page-treasury',
            category: 'pages',
            title: t('nav.treasury'),
            subtitle: isRtl ? 'الخزائن، البنوك وسندات القبض والصرف' : 'Cash Drawers, Banks & Vouchers',
            icon: _jsx(Wallet, { className: "w-4 h-4 text-emerald-600" }),
            onSelect: () => {
                navigate('/treasury');
                onClose();
            },
        },
        {
            id: 'page-accounting',
            category: 'pages',
            title: t('nav.accounting'),
            subtitle: isRtl ? 'شجرة الحسابات والقيود وميزان المراجعة' : 'Chart of Accounts & Journals',
            icon: _jsx(BookOpen, { className: "w-4 h-4 text-cyan-600" }),
            onSelect: () => {
                navigate('/accounting');
                onClose();
            },
        },
        {
            id: 'page-reports',
            category: 'pages',
            title: t('nav.reports'),
            subtitle: isRtl ? 'القوائم المالية وأعمار الديون' : 'Financial Statements & Aging',
            icon: _jsx(BarChart3, { className: "w-4 h-4 text-rose-600" }),
            onSelect: () => {
                navigate('/reports');
                onClose();
            },
        },
        {
            id: 'page-audit',
            category: 'pages',
            title: isRtl ? 'سجل التدقيق والأمان' : 'Enterprise Audit Log',
            subtitle: isRtl ? 'تتبع العمليات والمستخدمين' : 'Security & Change Trail',
            icon: _jsx(ShieldAlert, { className: "w-4 h-4 text-slate-600" }),
            onSelect: () => {
                navigate('/audit');
                onClose();
            },
        },
        // Quick Actions
        {
            id: 'action-new-sale',
            category: 'actions',
            title: isRtl ? 'إنشاء فاتورة بيع جديدة' : 'Create New Sales Invoice',
            subtitle: isRtl ? 'إصدار فاتورة ضريبية إلكترونية' : 'Issue Tax Electronic Invoice',
            icon: _jsx(Plus, { className: "w-4 h-4 text-blue-600" }),
            shortcut: 'Alt+N',
            onSelect: () => {
                navigate('/sales');
                onClose();
            },
        },
        {
            id: 'action-new-pos',
            category: 'actions',
            title: isRtl ? 'فتح شاشة الكاشير السريع (POS)' : 'Launch Fast POS Cashier',
            subtitle: isRtl ? 'بيع مباشر بالباركود' : 'Direct Barcode Checkout',
            icon: _jsx(Sparkles, { className: "w-4 h-4 text-emerald-600" }),
            onSelect: () => {
                navigate('/pos');
                onClose();
            },
        },
        {
            id: 'action-new-receipt',
            category: 'actions',
            title: isRtl ? 'تسجيل سند قبض نقدي' : 'Record Cash Receipt Voucher',
            subtitle: isRtl ? 'تحصيل دفعة نقدية من عميل' : 'Receive customer payment',
            icon: _jsx(Plus, { className: "w-4 h-4 text-emerald-600" }),
            onSelect: () => {
                navigate('/treasury');
                onClose();
            },
        },
        {
            id: 'action-toggle-lang',
            category: 'actions',
            title: isRtl ? 'Switch Language to English' : 'التبديل إلى اللغة العربية',
            subtitle: isRtl ? 'Change system interface to English' : 'تغيير واجهة النظام للعربية',
            icon: _jsx(Globe, { className: "w-4 h-4 text-indigo-600" }),
            onSelect: () => {
                toggleLanguage();
                onClose();
            },
        },
    ];
    const filteredItems = items.filter((item) => {
        if (!search.trim())
            return true;
        const query = search.toLowerCase();
        return (item.title.toLowerCase().includes(query) ||
            (item.subtitle && item.subtitle.toLowerCase().includes(query)) ||
            (item.shortcut && item.shortcut.toLowerCase().includes(query)));
    });
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : 0));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : filteredItems.length - 1));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const selected = filteredItems[selectedIndex];
            if (selected) {
                selected.onSelect();
            }
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex items-start justify-center pt-20 p-4 sm:p-6 bg-slate-950/40 backdrop-blur-xs animate-in fade-in duration-150", children: [_jsx("div", { className: "fixed inset-0", onClick: onClose, "aria-hidden": "true" }), _jsxs("div", { className: "relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200/90 overflow-hidden text-start z-10 animate-in zoom-in-95 duration-150", dir: isRtl ? 'rtl' : 'ltr', children: [_jsxs("div", { className: "p-3.5 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50", children: [_jsx(Search, { className: "w-5 h-5 text-slate-400 flex-shrink-0" }), _jsx("input", { ref: inputRef, type: "text", value: search, onChange: (e) => {
                                    setSearch(e.target.value);
                                    setSelectedIndex(0);
                                }, onKeyDown: handleKeyDown, placeholder: t('command.searchPlaceholder'), className: "w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none" }), _jsx("kbd", { className: "hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono font-medium text-slate-400 bg-white border border-slate-200 rounded-md shadow-2xs", children: "ESC" })] }), _jsx("div", { className: "max-h-96 overflow-y-auto p-2 space-y-1 divide-y divide-slate-100/50", children: filteredItems.length === 0 ? (_jsxs("div", { className: "py-12 text-center text-slate-400 text-xs", children: [_jsx(Search, { className: "w-8 h-8 mx-auto mb-2 text-slate-300 stroke-1" }), _jsx("p", { children: t('command.noResults') })] })) : (filteredItems.map((item, index) => {
                            const isSelected = index === selectedIndex;
                            return (_jsxs("button", { type: "button", onClick: item.onSelect, onMouseEnter: () => setSelectedIndex(index), className: `w-full p-2.5 rounded-xl flex items-center justify-between text-start transition-all cursor-pointer ${isSelected
                                    ? 'bg-blue-50/80 text-blue-900 border border-blue-200/60 shadow-2xs'
                                    : 'hover:bg-slate-50 text-slate-700 border border-transparent'}`, children: [_jsxs("div", { className: "flex items-center gap-3 min-w-0", children: [_jsx("div", { className: `w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-white shadow-2xs' : 'bg-slate-100 text-slate-500'}`, children: item.icon }), _jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "font-semibold text-xs text-slate-900 truncate", children: item.title }), item.subtitle && (_jsx("p", { className: "text-[11px] text-slate-500 truncate mt-0.5", children: item.subtitle }))] })] }), _jsxs("div", { className: "flex items-center gap-2 flex-shrink-0 ms-2", children: [item.shortcut && (_jsx("span", { className: "text-[10px] font-mono font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200", children: item.shortcut })), isSelected && (_jsx(ArrowRight, { className: `w-3.5 h-3.5 text-blue-600 ${isRtl ? 'rotate-180' : ''}` }))] })] }, item.id));
                        })) }), _jsxs("div", { className: "px-4 py-2 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400", children: [_jsx("span", { children: isRtl ? 'استخدم الأسهم ⬆ ⬇ للتنقل، و Enter للاختيار' : 'Use arrows ⬆ ⬇ to navigate, Enter to select' }), _jsx("span", { className: "font-mono text-[10px]", children: "WATAD ERP v1.0" })] })] })] }));
}
//# sourceMappingURL=CommandPalette.js.map